<template>
  <Sidebar :open="open" @overlay-click="closeMenu">
    <template #header>
      <div class="app-sidebar__header">
        <Button
          size="large"
          type="secondary"
          icon="crossmark"
          icon-only
          @click="closeMenu"
          data-qa="close-menu-button"
        />
        <Badge :label="messagesCounter" v-if="isPatient && features.inbox">
          <Button
            size="large"
            type="secondary"
            icon="mail"
            icon-only
            @click="onMessagesClicked"
            data-qa="messages-button"
          />
        </Badge>
        <Button
          v-else-if="isPractitioner"
          type="secondary"
          @click="openStationBoardDialog"
          data-qa="close-menu-button"
          >{{ $t('sidebar.show-station-board') }}
        </Button>
      </div>
      <img
        src="@/assets/myclienia-logo.png"
        alt="Logo"
        :height="56"
        :width="150"
        class="mt-10 mb-5"
      />
    </template>
    <div class="flex flex-col flex-grow justify-center items-center gap-1">
      <template v-if="isPatient">
        <RouterLink :to="{ name: HOME }" class="menu-card" data-qa="home-link" @click="closeMenu">
          <SvgIcon name="home" :size="24" />
          <span class="flex-grow mx-5">{{ t('sidebar.home') }}</span>
          <Badge :label="mealCounter || absenceCounter" visible inline bullet />
        </RouterLink>
        <RouterLink
          v-if="features.appointments"
          :to="{ name: APPOINTMENTS }"
          class="menu-card"
          data-qa="appointments-link"
          @click="closeMenu"
        >
          <SvgIcon name="calendar" :size="24" />
          <span class="flex-grow mx-5">{{ t('sidebar.appointments') }}</span>
          <Badge :label="appointmentsCounter" visible inline />
        </RouterLink>
        <RouterLink
          v-if="features.questionnaires"
          :to="{ name: SURVEYS }"
          class="menu-card"
          data-qa="survey-link"
          @click="closeMenu"
        >
          <SvgIcon name="questionnaire" :size="24" />
          <span class="flex-grow mx-5">{{ t('sidebar.questionnaires') }}</span>
          <Badge :label="surveysCounter" visible inline />
        </RouterLink>
        <RouterLink
          v-if="newsStore.hasNews && features.news"
          :to="{ name: NEWS }"
          class="menu-card"
          data-qa="info-link"
          @click="closeMenu"
        >
          <SvgIcon name="news" :size="24" />
          <span class="flex-grow mx-5">{{ t('sidebar.news') }}</span>
          <Badge :label="newsCounter" visible inline />
        </RouterLink>
        <RouterLink
          v-if="infoStore.hasInfoArticles && features.knowledge_base"
          :to="{ name: INFO }"
          class="menu-card"
          data-qa="info-link"
          @click="closeMenu"
        >
          <SvgIcon name="circle-question" :size="24" />
          <span class="flex-grow mx-5">{{ t('sidebar.info') }}</span>
        </RouterLink>
      </template>
      <template v-else-if="isPractitioner">
        <RouterLink
          :to="{ name: PATIENTS }"
          class="menu-card"
          data-qa="patients-link"
          @click="closeMenu"
        >
          <SvgIcon name="patients" :size="24" />
          <span class="flex-grow mx-5">{{ t('sidebar.patients') }}</span>
        </RouterLink>
        <RouterLink
          :to="{ name: MESSAGING }"
          class="menu-card"
          data-qa="patients-link"
          @click="closeMenu"
        >
          <SvgIcon name="messaging" :size="24" />
          <span class="flex-grow mx-5">{{ t('sidebar.messaging') }}</span>
        </RouterLink>
      </template>
    </div>
    <div class="flex flex-col pb-16 pt-8 border-t border-gray-200">
      <RouterLink
        :to="{ name: SETTINGS }"
        class="system-menu-item"
        @click="closeMenu"
        data-qa="settings-link"
      >
        <SvgIcon name="cog" :size="16" />
        {{ t('sidebar.settings') }}
      </RouterLink>
      <RouterLink
        :to="{ name: PROFILE }"
        class="system-menu-item"
        @click="closeMenu"
        data-qa="profile-link"
      >
        <SvgIcon name="user" :size="16" />
        {{ t('sidebar.my-profile') }}
      </RouterLink>
    </div>
    <template #footer>
      <p class="text-base text-gray-700">
        {{ t('common.clienia-ag', { year: new Date().getFullYear() }) }}
      </p>
    </template>
  </Sidebar>
</template>

<script setup lang="ts">
import { Preferences } from '@capacitor/preferences'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import Badge from '@/components/common/badge/Badge.vue'
import Button from '@/components/common/buttons/Button.vue'
import SvgIcon from '@/components/common/SvgIcon.vue'
import { useUserRoles } from '@/composables/useUserRoles'
import { CUSTOM_EVENTS, logEvent } from '@/firebase-analytics'
import { STATION_BOARD } from '@/modules/absence/routes'
import { useAbsenceStore } from '@/modules/absence/store'
import { APPOINTMENTS } from '@/modules/appointments/routes'
import { useAuthStore } from '@/modules/auth/store'
import { useCounterStore } from '@/modules/counter/store'
import { HOME } from '@/modules/home/routes'
import { INFO } from '@/modules/info/routes'
import { useInfoStore } from '@/modules/info/store'
import { PROFILE } from '@/modules/my-profile/routes'
import { NEWS } from '@/modules/news/routes'
import { useNewsStore } from '@/modules/news/store'
import { MESSAGING } from '@/modules/patient-messaging/routes'
import { PATIENTS } from '@/modules/patients/routes'
import { SETTINGS } from '@/modules/settings/routes'
import { SURVEYS } from '@/modules/survey/routes'

import Sidebar from '../common/sidebar/Sidebar.vue'

defineProps<{
  open: boolean
}>()

const emit = defineEmits<{
  'update:open': [value: boolean]
  'open-inbox': []
}>()

const { t } = useI18n()
const store = useAuthStore()
const counterStore = useCounterStore()
const absenceStore = useAbsenceStore()
const newsStore = useNewsStore()
const infoStore = useInfoStore()
const { isPatient, isPractitioner } = useUserRoles()
const router = useRouter()

const appointmentsCounter = computed(() => counterStore.counter?.new_appointments || 0)
const newsCounter = computed(() => counterStore.counter?.news || 0)
const surveysCounter = computed(() => counterStore.counter?.surveys || 0)
const messagesCounter = computed(() => counterStore.counter?.messages || 0)
const mealCounter = computed(() => +!!counterStore.counter?.upcoming_meal || 0)
const absenceCounter = computed(() => Number(!!counterStore.counter?.absences) || 0)
const features = computed(() => store?.features ?? {})

const closeMenu = () => {
  emit('update:open', false)
}

const openStationBoardDialog = async () => {
  closeMenu()
  const boardFromStorage = await Preferences.get({ key: 'stationBoard' }).then((res) => res.value)
  const board = boardFromStorage
    ? JSON.parse(boardFromStorage)
    : await absenceStore.toggleStationBoard(true)

  if (!boardFromStorage) {
    await Preferences.set({
      key: 'stationBoard',
      value: JSON.stringify(board),
    })
  }

  await router.push({ name: STATION_BOARD })
}

const onMessagesClicked = () => {
  logEvent(CUSTOM_EVENTS.MODULE_VISITED, {
    module: 'messages',
  })
  closeMenu()
  emit('open-inbox')
}
</script>

<style scoped lang="postcss">
.app-sidebar__header {
  @apply flex justify-between items-center safe-top;
}
.menu-card {
  @apply flex text-key-500 p-5 bg-white shadow-card rounded-md w-full text-lg font-bold items-center;

  .meal-notification {
    @apply text-gray-600 font-regular text-base;
  }

  &.router-link-active {
    @apply bg-accent-500 text-white;
    .meal-notification {
      @apply text-white;
    }
  }

  &:not(.router-link-active):hover {
    @apply shadow-card-hover text-key-700;
  }
}

.system-menu-item {
  @apply mb-5 flex items-start text-base font-bold text-key-600 hover:text-key-800;

  .svg-icon {
    @apply flex-shrink-0 mr-1 mt-[2px];
  }

  &.router-link-active {
    @apply text-gray-800;
  }
}
</style>
